import React, { useLayoutEffect } from 'react';

import {
  type CommentThreadEvent,
  type CommentStore,
} from '../comment-store/comment-store-interface.ts';
import { type CommentContentType } from '../comment-types.ts';
import { type CommentModel } from '../model.ts';

export function useThread(
  store: CommentStore,
  threadId: string,
  externalId: string | undefined,
  initialComments: CommentModel[],
  cloudId?: string,
  contentId?: string,
  contentType?: CommentContentType,
  activationId?: string,
) {
  const [comments, setComments] = React.useState<CommentModel[]>(initialComments);

  useLayoutEffect(() => {
    const subscriber = (evt: CommentThreadEvent) => {
      if (
        evt.type === 'threadResolved' ||
        evt.type === 'errored' ||
        evt.type === 'rootCommentChanged' ||
        evt.type === 'deletedReplyFromThread' ||
        evt.type === 'addedReplyToThread' ||
        evt.type === 'threadAssociated' ||
        evt.type === 'threadSelected'
      ) {
        return;
      }

      const nextThreadId = evt.threadId;
      if (nextThreadId !== threadId) {
        return;
      }

      const nextThread = store.getThread(threadId);
      if (!nextThread || nextThread.status === 'loading') {
        return;
      }

      setComments([...nextThread.comments]);
    };

    store.subscribe(subscriber);
    store.initializeThread(
      threadId,
      externalId,
      cloudId,
      contentId,
      contentType,
      activationId,
    );

    return () => store.unsubscribe(subscriber);
  }, [store, threadId, externalId, cloudId, contentId, contentType, activationId]);

  return comments;
}
